import React, { useEffect, useState, useCallback } from "react";
import { getTrash, restoreTrash, purgeTrash } from "../api";
import { formatSize, formatDateTime } from "../media";
import { IcoTrash, IcoX, IcoRestore, IcoFolder, IcoImage } from "./Icons";

const daysLeft = (expiresAt) =>
  Math.max(0, Math.ceil((expiresAt - Date.now()) / (24 * 60 * 60 * 1000)));

// Thùng rác: file/thư mục đã xóa được máy chủ giữ lại một thời gian (keepDays)
// trước khi xóa hẳn. Ở đây có thể khôi phục về chỗ cũ hoặc xóa hẳn ngay.
export default function TrashModal({ onClose, onChanged, showPopup }) {
  const [items, setItems] = useState([]);
  const [keepDays, setKeepDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmPurgeId, setConfirmPurgeId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTrash();
      setItems(data.items || []);
      if (data.keepDays) setKeepDays(data.keepDays);
    } catch {
      setError("Không tải được thùng rác.");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const restore = async (item) => {
    setBusyId(item.id);
    try {
      const res = await restoreTrash(item.id);
      const renamed = res.restoredPath && res.restoredPath !== item.originalPath;
      showPopup(
        renamed
          ? `Đã khôi phục thành "${res.restoredPath}" (chỗ cũ đã có file trùng tên)`
          : `Đã khôi phục "${item.name}"`
      );
      await load();
      onChanged();
    } catch {
      showPopup("Khôi phục thất bại!", "error");
    }
    setBusyId(null);
  };

  const purge = async (item) => {
    setBusyId(item.id);
    try {
      await purgeTrash(item.id);
      showPopup(`Đã xóa hẳn "${item.name}"`);
      await load();
    } catch {
      showPopup("Xóa thất bại!", "error");
    }
    setConfirmPurgeId(null);
    setBusyId(null);
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Thùng rác">
      <div className="modal-card modal-wide modal-tall">
        <div className="modal-head">
          <span className="modal-ico"><IcoTrash size={20} /></span>
          <div>
            <h3>Thùng rác</h3>
            <p>File đã xóa được giữ {keepDays} ngày rồi mới xóa hẳn. Khôi phục sẽ đưa file về đúng thư mục cũ.</p>
          </div>
          <button className="icon-btn ghost modal-x" onClick={onClose} aria-label="Đóng">
            <IcoX size={20} />
          </button>
        </div>

        <div className="modal-body modal-scroll">
          {loading && <p className="list-empty">Đang tải…</p>}
          {!loading && error && <p className="list-empty">{error}</p>}
          {!loading && !error && items.length === 0 && (
            <div className="list-empty">
              <span className="empty-art small"><IcoTrash size={24} strokeWidth={1.4} /></span>
              <p>Thùng rác trống</p>
            </div>
          )}

          {!loading && !error && items.length > 0 && (
            <ul className="trash-list">
              {items.map((item) => (
                <li key={item.id} className="trash-row">
                  <span className={`trash-ico ${item.type === "folder" ? "folder" : ""}`}>
                    {item.type === "folder" ? <IcoFolder size={18} /> : <IcoImage size={18} />}
                  </span>
                  <div className="trash-main">
                    <div className="trash-name" title={item.originalPath}>{item.name}</div>
                    <div className="trash-meta">
                      {item.originalPath}
                      {item.size > 0 && <> · {formatSize(item.size)}</>}
                    </div>
                    <div className="trash-meta">
                      Xóa lúc {formatDateTime(item.deletedAt)} · còn {daysLeft(item.expiresAt)} ngày
                    </div>
                  </div>
                  <div className="trash-actions">
                    {confirmPurgeId === item.id ? (
                      <>
                        <button className="btn btn-danger btn-sm" disabled={busyId === item.id} onClick={() => purge(item)}>
                          Xóa hẳn?
                        </button>
                        <button className="btn btn-quiet btn-sm" onClick={() => setConfirmPurgeId(null)}>
                          Không
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-soft btn-sm" disabled={busyId === item.id} onClick={() => restore(item)}>
                          <IcoRestore size={15} /> <span>Khôi phục</span>
                        </button>
                        <button className="btn btn-quiet btn-sm" disabled={busyId === item.id} onClick={() => setConfirmPurgeId(item.id)}>
                          Xóa hẳn
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn btn-quiet" onClick={onClose}>Đóng</button>
        </div>
      </div>
    </div>
  );
}
